import { useId, useRef, useState } from "react";
import { X } from "lucide-react";

import { COARSE_HIT_AREA, CONTROL_RADIUS, cx } from "./controlStyles";
import { Kbd } from "./Kbd";

// A list of strings (addresses, numbers, ids) edited as removable chips
// with an inline field. Enter, comma, semicolon or Tab commits what is
// typed AS ONE entry — spaces stay inside it ("+44 7470 243067", "Jane
// Doe <jane@x.com>"). A paste is split on commas, semicolons and new lines;
// only when every piece of a space-separated paste is valid (a column of
// addresses) is it split on spaces too. Backspace in the empty field
// removes the last chip; duplicates are dropped; Enter while an IME is
// composing does nothing. `validate` may return false or a message: the
// entry is still kept (so nothing typed is lost) but flagged in the danger
// colour with the message as its tooltip and in the accessible description.
//
//   <TokenInput ariaLabel="Allowed senders" values={list} onChange={setList}
//     placeholder="Add a number"
//     validate={(v) => /^\+\d{7,15}$/.test(v) || "Use +44…"} />
//
// Once there are chips the field keeps a short placeholder
// (`addPlaceholder`, "Add…" by default: short enough for the narrow field a
// phone leaves beside a long chip) so it still reads as a place to type,
// and while something is typed an "Enter to add" hint shows (not on touch).

interface TokenInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  ariaLabel: string;
  placeholder?: string;
  /** Placeholder once the list has entries; keep it short ("Add…"). */
  addPlaceholder?: string;
  /** true = valid; false or a message = invalid. */
  validate?: (value: string) => boolean | string;
  disabled?: boolean;
  className?: string;
}

const SEPARATORS = /[,;\r\n]+/;
const SPACES = /\s+/;

export function TokenInput({
  values,
  onChange,
  ariaLabel,
  placeholder,
  addPlaceholder = "Add…",
  validate,
  disabled,
  className,
}: TokenInputProps) {
  const [draft, setDraft] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const hintId = useId();

  function problem(v: string): string | null {
    if (!validate) return null;
    const r = validate(v);
    return r === true ? null : typeof r === "string" ? r : "Not valid";
  }

  function pieces(raw: string, splitSpaces = false): string[] {
    const parts = raw.split(SEPARATORS).map((p) => p.trim()).filter(Boolean);
    if (!splitSpaces || !validate) return parts;
    // "a@x.com b@y.com" pasted from a column: split on spaces only when
    // every word is itself a valid entry (a phone number is not).
    const words = parts.flatMap((p) => p.split(SPACES)).filter(Boolean);
    return words.length > parts.length && words.every((w) => problem(w) === null) ? words : parts;
  }

  function add(parts: string[]) {
    setDraft("");
    if (!parts.length) return;
    const next = [...values];
    for (const p of parts) if (!next.includes(p)) next.push(p);
    if (next.length !== values.length) onChange(next);
  }

  function remove(i: number) {
    onChange(values.filter((_, j) => j !== i));
    input.current?.focus();
  }

  const invalid = values.map(problem);
  const invalidCount = invalid.filter(Boolean).length;

  return (
    <div className={className}>
      <div
        className={cx(
          "flex min-h-[40px] w-full flex-wrap items-center gap-1.5 border border-control bg-inset px-1.5 py-[5px]",
          CONTROL_RADIUS,
          "focus-within:border-[color:var(--c-focus)] focus-within:shadow-[0_0_0_3px_color-mix(in_srgb,var(--c-focus)_25%,transparent)]",
          disabled && "opacity-45",
        )}
        onClick={() => input.current?.focus()}
      >
        {values.map((v, i) => {
          const bad = invalid[i];
          return (
            <span
              key={`${v}-${i}`}
              title={bad ?? undefined}
              className={cx(
                "inline-flex h-[26px] max-w-full items-center gap-1 rounded-pill border pl-2.5 pr-1 text-sm",
                bad ? "border-[color:var(--c-danger)] bg-danger/10 text-danger" : "border-line bg-raised text-fg",
              )}
            >
              <span className="truncate">{v}</span>
              {bad && <span className="sr-only">(not valid: {bad})</span>}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${v}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(i);
                  }}
                  className={cx(
                    "relative grid h-[20px] w-[20px] shrink-0 place-items-center rounded-full text-fg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus [@media(hover:hover)]:hover:bg-raised [@media(hover:hover)]:hover:text-fg",
                    // 44px to a finger without growing the chip.
                    COARSE_HIT_AREA,
                  )}
                >
                  <X aria-hidden className="h-3 w-3" strokeWidth={2} />
                </button>
              )}
            </span>
          );
        })}
        <input
          ref={input}
          value={draft}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={invalidCount ? hintId : undefined}
          placeholder={values.length ? addPlaceholder : placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter also confirms an IME candidate; that is not a commit.
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            const commit = e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab";
            if (commit && draft.trim()) {
              e.preventDefault();
              add(pieces(draft));
            } else if (e.key === "Enter" || e.key === "," || e.key === ";") {
              e.preventDefault();
            } else if (e.key === "Backspace" && !draft && values.length) {
              remove(values.length - 1);
            }
          }}
          onPaste={(e) => {
            // One value (even with spaces) pastes into the field as usual;
            // a list becomes chips straight away.
            const text = e.clipboardData.getData("text").trim();
            if (SEPARATORS.test(text) || pieces(text, true).length > 1) {
              e.preventDefault();
              add(pieces(draft + text, true));
            }
          }}
          onBlur={() => draft.trim() && add(pieces(draft))}
          className="h-[26px] min-w-[120px] flex-1 bg-transparent px-1.5 text-sm text-fg outline-none placeholder:text-fg-subtle"
        />
        {draft.trim() && !disabled && (
          <span aria-hidden className="flex shrink-0 items-center gap-1 pr-1 text-xs text-fg-subtle [@media(pointer:coarse)]:hidden">
            <Kbd>Enter</Kbd> to add
          </span>
        )}
      </div>
      {invalidCount > 0 && (
        <ul id={hintId} className="mt-1 space-y-0.5 text-xs text-danger">
          {values.map((v, i) =>
            invalid[i] ? (
              <li key={`${v}-${i}`}>
                {v}: {invalid[i]}
              </li>
            ) : null,
          )}
        </ul>
      )}
    </div>
  );
}
